/**
 * Publishes filter and sort use on collection and search pages as Shopify
 * custom events, so a Custom Pixel (Settings → Customer events) can forward
 * them to GA4. The Google & YouTube app's pixel runs sandboxed and never sees
 * the theme, so this is the only way filter use reaches GA.
 *
 * Both filter UIs keep their state in the URL without reloading: the PLP
 * (assets/facets.js) as `filter.v.option.size=M` / `sort_by=…`, Algolia search
 * as `refinementList[options.product_size][0]=M` / `sortBy=…`. Watching the
 * URL covers both without hooking either script.
 *
 * Events:
 *   filter_applied { surface, filter, value, collection }  one per value added
 *   sort_changed   { surface, sort, collection }
 */
(function () {
  const path = window.location.pathname;
  const surface = path.startsWith('/search') ? 'search' : path.includes('/collections/') ? 'collection' : null;
  if (!surface) return;

  const collection = surface === 'collection' ? path.split('/collections/')[1]?.split('/')[0] ?? '' : '';

  /**
   * One name per filter across both UIs, so GA groups them: the PLP's
   * "option.product size" and Algolia's "options.product_size" both become
   * "product_size", and the PLP's "price.gte"/"price.lte" become "price".
   * @param {string} name
   */
  function normalize(name) {
    return name
      .toLowerCase()
      .replace(/^options?\./, '')
      .replace(/\.(gte|lte)$/, '')
      .replace(/\s+/g, '_');
  }

  /**
   * @param {string} search
   * @returns {{ filters: Set<string>, sort: string }} filters as "name\u0000value"
   */
  function read(search) {
    const filters = new Set();
    let sort = '';
    for (const [key, value] of new URLSearchParams(search)) {
      if (key === 'sort_by' || key === 'sortBy') {
        sort = value;
        continue;
      }
      let name = null;
      if (key.startsWith('filter.')) {
        name = key.slice('filter.'.length).replace(/^[vp]\./, '');
      } else {
        const match = key.match(/^(?:refinementList|range|hierarchicalMenu|toggle)\[([^\]]+)\]/);
        if (match) name = match[1];
      }
      if (name && value !== '') filters.add(`${normalize(name)}\u0000${value}`);
    }
    return { filters, sort };
  }

  /** @param {string} name @param {Record<string, string>} data */
  function publish(name, data) {
    try {
      window.Shopify?.analytics?.publish?.(name, data);
    } catch (error) {
      // Analytics must never break filtering.
    }
  }

  let previous = read(window.location.search);

  function check() {
    const current = read(window.location.search);
    for (const entry of current.filters) {
      if (previous.filters.has(entry)) continue;
      const [filter, value] = entry.split('\u0000');
      publish('filter_applied', { surface, filter, value, collection });
    }
    if (current.sort !== previous.sort && current.sort) {
      publish('sort_changed', { surface, sort: current.sort, collection });
    }
    previous = current;
  }

  for (const method of /** @type {const} */ (['pushState', 'replaceState'])) {
    const original = history[method];
    history[method] = function (...args) {
      const result = original.apply(this, args);
      check();
      return result;
    };
  }
  // Back/forward restores an earlier state rather than applying a filter.
  window.addEventListener('popstate', () => {
    previous = read(window.location.search);
  });
})();
